import React, { useState, useEffect } from 'react';
import deepEqual from 'deep-equal';

// Material-ui
import { Grid } from '@material-ui/core';

// Components
import Filters, { FilterProps } from '@components/UI-KIT/Filters';
import SelectFilter from '@/src/components/UI-KIT/Filters/Inputs/SelectFilter';
import MultiSelectFilter from '@components/UI-KIT/Filters/Inputs/MultiSelectFilter';
import AmountFilter from '@components/UI-KIT/Filters/Inputs/AmountFilter';
import DateFilter from '@components/UI-KIT/Filters/Inputs/DateFilter';

// Types
import { TransactionsFilters, TransactionsFiltersDefault } from '@services/account-transaction';
import { SelectOption } from '@components/UI-KIT/Inputs/SelectInput';
import { Account } from '@services/account';
import { Property, fetchProperties } from '@services/property';

// Services
import { fetchConcepts, Concept } from '@services/concept';

// Utils
import { formatAddress } from '@helpers/format-address';

interface Props extends FilterProps<TransactionsFilters> {
    accounts: Account[];
}

const TransactionsTableFilters: React.FC<Props> = ({
    accounts,
    filters,
    setFilters,
    handleFetchData,
    resetFilters
}) => {
    const [ conceptOptions, setConceptOptions ] = useState<SelectOption[]>([]);
    const [ propertyOptions, setPropertyOptions ] = useState<SelectOption[]>([]);

    useEffect(() => {
        (async () => {
            const { data: { items: concepts }} = await fetchConcepts(0, 0);
            setConceptOptions(concepts.filter((c: Concept) => c.visibility).map((c: Concept) => ({
                label: c.name,
                value: c.id
            })));

            const { data: { items: properties }} = await fetchProperties(0, 0);
            setPropertyOptions(properties.map((p: Property) => ({
                label: `NC ${p.externalReference} - ${formatAddress(p.address)}`,
                value: p.id
            })));
        })();
    }, []);

    const transactionTypes = [
        { label: 'Egreso', value: 'Outgoing' },
        { label: 'Ingreso', value: 'Incoming' },
        { label: 'Movimiento', value: 'Movement' }
    ];

    const accountOptions: SelectOption[] = accounts.map(a => ({ label: a.name, value: a.id }));

    const resetDisabled = deepEqual(filters, TransactionsFiltersDefault(), { strict: true });

    return (
        <Filters
            resetFilters={resetFilters}
            resetDisabled={resetDisabled}
            handleFetchData={handleFetchData}
        >
            <Grid
                container
                direction="column"
                style={{ height: '23rem' }}
            >
                <Grid
                    item
                    container
                >
                    <SelectFilter
                        label="Tipo de transacción"
                        value={filters.type}
                        options={transactionTypes}
                        onChange={type => setFilters(prevState => ({ ...prevState, type }))}
                    />
                </Grid>
                <Grid
                    item
                    container
                >
                    <Grid
                        container
                        alignItems="center"
                        wrap="nowrap"
                    >
                        <Grid
                            item
                        >
                            <AmountFilter
                                label="Monto mínimo"
                                value={filters.minAmount}
                                onChange={minAmount => setFilters(prevState => ({ ...prevState, minAmount }))}
                            />
                        </Grid>
                        <Grid
                            item
                        >
                            <AmountFilter
                                label="Monto máximo"
                                value={filters.maxAmount}
                                onChange={maxAmount => setFilters(prevState => ({ ...prevState, maxAmount }))}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid
                    item
                    container
                >
                    <MultiSelectFilter
                        label="Cuentas"
                        values={filters.accounts}
                        options={accountOptions}
                        onChange={accounts => setFilters(prevState => ({ ...prevState, accounts }))}
                        checkbox
                    />
                </Grid>
                <Grid
                    item
                    container
                >
                    <Grid
                        container
                        wrap="nowrap"
                    >
                        <Grid
                            item
                        >
                            <DateFilter
                                label="Desde"
                                value={filters.fromDate}
                                onChange={fromDate => setFilters(prevState => ({ ...prevState, fromDate }))}
                            />
                        </Grid>
                        <Grid
                            item
                        >
                            <DateFilter
                                label="Hasta"
                                value={filters.toDate}
                                onChange={toDate => setFilters(prevState => ({ ...prevState, toDate }))}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid
                    item
                >
                    <MultiSelectFilter
                        label="Conceptos"
                        values={filters.concepts}
                        options={conceptOptions}
                        onChange={concepts => setFilters(prevState => ({ ...prevState, concepts }))}
                        checkbox
                    />
                </Grid>
                <Grid
                    item
                >
                    <MultiSelectFilter
                        label="Propiedades"
                        values={filters.properties}
                        options={propertyOptions}
                        onChange={properties => setFilters(prevState => ({ ...prevState, properties }))}
                        checkbox
                    />
                </Grid>
            </Grid>
        </Filters>
    );
};

export default TransactionsTableFilters;
